import { Chip } from '@mui/material'
import { Datagrid, DateInput, FunctionField, List, SelectInput, TextInput, TopToolbar, useTranslate } from 'react-admin'

import { buildEvidenceSteps, InspectionState } from '../../domain/activeInspection'
import { formatDay } from '../../domain/activeInspectionCells'
import { INSPECTION_LEVELS } from '../../domain/inspectionLevels'
import { INSPECTION_LOCATIONS } from '../../domain/inspectionLocations'
import { INSPECTION_SERVICES } from '../../domain/inspectionServices'
import { convertToInspection, HistoricalInspection } from '../../models/inspection'
import { EvidenceStepStrip } from '../atoms/EvidenceStepStrip'
import { HistoricalInspectionExportButton } from '../molecules/HistoricalInspectionExportButton'

export const HistoricalInspectionList = () => {
  const translate = useTranslate()
  const possibleLocations = INSPECTION_LOCATIONS.map(location => ({
    id: location,
    name: translate(`resources.historical_inspections.enums.location.${location}`),
  }))
  // Spelled out in full here, unlike the column beside it: a dropdown has the
  // room, and the full name is what tells the four transvase levels apart.
  const possibleLevels = INSPECTION_LEVELS.map(level => ({
    id: level,
    name: translate(`resources.historical_inspections.enums.inspection_level.${level}`),
  }))
  // Services carry no translated name anywhere in the app: the Operativa column
  // prints the raw code, so the dropdown offers the raw code too.
  const possibleServices = INSPECTION_SERVICES.map(service => ({ id: service, name: service }))
  // Ordered to follow the columns below — fecha, contenedor, ubicación, nivel,
  // servicios — so the filter bar and the table read the same way round, as on
  // the Operativa screen.
  const filters = [
    <DateInput key="day" source="day" label="resources.historical_inspections.fields.day" alwaysOn />,
    <TextInput key="container" source="container" label="resources.historical_inspections.fields.container" alwaysOn />,
    <SelectInput
      key="location"
      source="location"
      label="resources.historical_inspections.fields.location"
      alwaysOn
      choices={possibleLocations}
    />,
    <SelectInput
      key="inspection_level"
      source="inspection_level"
      label="resources.historical_inspections.fields.inspection_level"
      alwaysOn
      choices={possibleLevels}
    />,
    <SelectInput
      key="services"
      source="services"
      label="resources.historical_inspections.fields.services"
      alwaysOn
      choices={possibleServices}
    />,
  ]

  return (
    <List
      filters={filters}
      sort={{ field: 'day', order: 'DESC' }}
      perPage={50}
      // The toolbar carries only Exportar: every filter is always on, so there is
      // no filter button, and the resource has no create. Exportar is our own —
      // React Admin's writes the raw record and stops at 1000 rows.
      actions={
        <TopToolbar>
          <HistoricalInspectionExportButton />
        </TopToolbar>
      }
    >
      {/* The row itself opens the record, so no cell is spent on an eye. */}
      <Datagrid bulkActionButtons={false} rowClick="show">
        <FunctionField<HistoricalInspection>
          label={translate('resources.historical_inspections.fields.day')}
          render={record => formatDay(record.day)}
        />
        <FunctionField<HistoricalInspection>
          label={translate('resources.historical_inspections.fields.container')}
          render={record => <strong>{record.container}</strong>}
        />
        <FunctionField<HistoricalInspection>
          label={translate('resources.historical_inspections.fields.location')}
          render={record => {
            const location = record.location

            // Try to translate location
            try {
              const translated = translate(`resources.historical_inspections.enums.location.${location}`)
              if (translated !== `resources.historical_inspections.enums.location.${location}`) {
                return translated
              }
            } catch (error) {
              console.warn('Location translation error:', error)
            }

            return location
          }}
        />
        {/*
          Short code on the chip, full name on its title — the same pairing the
          Operativa table uses, so a level reads identically on both screens.
        */}
        <FunctionField<HistoricalInspection>
          source="inspection_level"
          label={translate('resources.historical_inspections.fields.inspection_level')}
          render={record => (
            <Chip
              size="small"
              label={translate(`resources.historical_inspections.enums.inspection_level_short.${record.inspection_level}`)}
              title={translate(`resources.historical_inspections.enums.inspection_level.${record.inspection_level}`)}
            />
          )}
        />
        {/*
          Rendered exactly as the Operativa column renders it — the codes joined
          by commas, an em dash when there are none — so an inspection reads the
          same before and after it is archived. Not sortable: PostgREST would
          order the rows by the array itself, which is not an order a reader of
          this column would expect.
        */}
        <FunctionField<HistoricalInspection>
          source="services"
          sortable={false}
          label={translate('resources.historical_inspections.fields.services')}
          render={record => (record.services.length > 0 ? record.services.join(', ') : '—')}
        />
        {/*
          The evidence state machine, built through the same shared flow model
          the Operativa table builds it from. An archived inspection is
          FINALIZADO by definition, which is the state the strip needs in order
          to decide what a step left without evidence reads as.
        */}
        <FunctionField<HistoricalInspection>
          label={translate('resources.historical_inspections.fields.completeness')}
          render={record => (
            <EvidenceStepStrip steps={buildEvidenceSteps(convertToInspection(record).summaryView, InspectionState.FINISHED)} />
          )}
        />
      </Datagrid>
    </List>
  )
}
